import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { Button, Checkbox, Chip, IconButton, Stack, Tooltip, Typography } from '@curves/ui-base';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GradientColorHeader } from '@Local/Components/Dialog/';

import useClubApi from '@Apis/useClubApi';
import { ACCOUNT_ENSURE_STATUS_TEXT_MAP } from '@Local/ClubCompany/staticData';

const ACCOUNT_META = [
    { key: 'common', label: '通用' },
    { key: 'owner', label: '加盟主' },
    { key: 'admin', label: '管理者' },
];

/** 未建立(status 為 null)或上次建立失敗(failed)的帳號才可勾選；已存在(existing)/已建立(created)不可再選 */
const isSelectable = (status) => status === null || status === 'failed';

/**
 * 依店舖代碼與目前總覽帳號旗標，組出三個固定帳號的初始顯示狀態
 * 已建立者不顯示 checkbox；未建立者顯示 checkbox 且預設勾選
 */
const buildInitialAccounts = (clubCode, hasCommonAccount, hasOwnerAccount, hasAdminAccount) => {
    const code = (clubCode || '').toLowerCase();
    const existsMap = { common: hasCommonAccount, owner: hasOwnerAccount, admin: hasAdminAccount };
    const userNameMap = { common: code, owner: `${code}_owner`, admin: `${code}_admin` };

    return ACCOUNT_META.map(({ key, label }) => {
        const status = existsMap[key] ? 'existing' : null;
        return { key, label, userName: userNameMap[key], status, checked: isSelectable(status) };
    });
};

/**
 * 檢查／補建店舖固定帳號 Dialog
 * 未建立的帳號顯示 checkbox(預設全勾)，可選擇性建立；已建立的帳號僅顯示狀態與複製功能，不可再建立
 * 三個帳號皆已建立時，畫面上不渲染任何會打建立 API 的按鈕
 */
const EnsureAccountsDialog = React.forwardRef((props, ref) => {
    const { open = false, onClose, sourceData: clubData, refresh } = props;
    const { postEnsureAccountsApi } = useClubApi();

    const [accounts, setAccounts] = useState([]);
    const [copiedKey, setCopiedKey] = useState('');

    const { clubID = 0, clubCode = '', hasCommonAccount = false, hasOwnerAccount = false, hasAdminAccount = false } = clubData || {};

    useEffect(() => {
        if (open) {
            setAccounts(buildInitialAccounts(clubCode, hasCommonAccount, hasOwnerAccount, hasAdminAccount));
            setCopiedKey('');
        }
        // eslint-disable-next-line
    }, [open]);

    const handleToggle = (key) => {
        setAccounts((prev) => prev.map((a) => (a.key === key && isSelectable(a.status) ? { ...a, checked: !a.checked } : a)));
    };

    const handleConfirm = async () => {
        const get = (key) => accounts.find((a) => a.key === key);
        const selection = {
            common: isSelectable(get('common').status) && get('common').checked,
            owner: isSelectable(get('owner').status) && get('owner').checked,
            admin: isSelectable(get('admin').status) && get('admin').checked,
        };

        const resp = await postEnsureAccountsApi(clubID, selection);
        // 整體性錯誤(如預設密碼未設定)時 useAxios 已顯示錯誤訊息，resp 為 undefined/null，此處不更新畫面
        if (!resp) return;

        // 只更新本次有回值(非 null)的帳號，未選建者維持原狀
        setAccounts((prev) =>
            prev.map((a) => {
                const r = resp[a.key];
                if (!r) return a;
                return { ...a, userName: r.userName ?? a.userName, status: r.status, checked: r.status === 'failed' };
            })
        );

        if (typeof refresh === 'function') refresh();

        // 全成功才關閉 dialog（比照站台 ClubDialog 慣例）；任一 failed 則不關，維持可重試
        const hasFailed = ['common', 'owner', 'admin'].some((k) => resp[k]?.status === 'failed');
        if (!hasFailed && typeof onClose === 'function') onClose();
    };

    const handleCopy = (key, userName) => {
        if (!userName) return;
        navigator.clipboard.writeText(userName);
        setCopiedKey(key);
    };

    const hasSelectable = accounts.some(({ status }) => isSelectable(status));
    const checkedCount = accounts.filter(({ status, checked }) => isSelectable(status) && checked).length;

    return (
        <Dialog open={open} fullWidth maxWidth={'sm'}>
            <GradientColorHeader onClose={onClose}>檢查／補建店舖固定帳號</GradientColorHeader>
            <DialogContent>
                <Grid container spacing={2}>
                    {accounts.map(({ key, label, userName, status, checked }) => (
                        <Grid xs={12} item key={key}>
                            <Stack direction={'row'} spacing={2} alignItems={'center'}>
                                {isSelectable(status) ? (
                                    <Checkbox checked={checked} onChange={() => handleToggle(key)} />
                                ) : (
                                    <Checkbox checked disabled />
                                )}
                                <Typography sx={{ width: '5.6rem' }}>{label}</Typography>
                                <Typography sx={{ flex: 1 }}>{userName}</Typography>
                                {status && (
                                    <Chip
                                        size={'small'}
                                        label={ACCOUNT_ENSURE_STATUS_TEXT_MAP[status] ?? status}
                                        color={status === 'failed' ? 'error' : status === 'created' ? 'success' : 'default'}
                                    />
                                )}
                                <Tooltip title={copiedKey === key ? '已複製' : '複製帳號'}>
                                    <span>
                                        <IconButton size={'small'} onClick={() => handleCopy(key, userName)} disabled={!userName}>
                                            <ContentCopyIcon fontSize={'small'} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            {hasSelectable && (
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleConfirm} disabled={checkedCount === 0}>
                        {`建立勾選的 ${checkedCount} 個帳號`}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
});

export default EnsureAccountsDialog;
